import { faEye } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { formatInTimeZone } from 'date-fns-tz'
import { useFormikContext } from 'formik'
import { FormattedNumber } from 'react-intl'
import { styled } from 'styled-components'

import type { OrchestratedOfferingListingDetails } from '@b-stock/fe-core/bstock-next/queries'
import {
  Duration,
  FormattedMessage,
  parseOptionalISODate,
} from '@b-stock/fe-core/bstock-react'
import { Typography } from '@b-stock/fe-core/design-system'

import { type MakeAnOfferFormValues } from '../../offering/SimpleOfferActionModule/MakeAnOfferFormProvider'
import {
  AmountValue,
  Column,
  CountdownValue,
  Header,
  InfoIcon,
  Label,
  MsrpRow,
  MsrpText,
  StatItem,
  SubRow,
} from '../ui'
import { getIsSpotPriceOffer, useOfferAmount } from './useOfferAmount'
import type { OfferDecisionState } from './useOfferDecisionState'

const ns = 'Listing.actionColumn.actionCard.offer'

const clientTimeZone = new Intl.DateTimeFormat().resolvedOptions().timeZone
const dateFormat = 'MMM d, yyyy'
const timeFormat = 'h:mm aa z'

const EndedValue = styled.span`
  ${Typography.Subtitle1}
  color: white;
  opacity: 0.7;
`

/**
 * countdown (or end date, once decided) + views on left; minimum/current amount
 * + % of MSRP on right.
 */
export const SimpleOfferHeader = ({
  listing,
  state,
}: {
  listing: OrchestratedOfferingListingDetails
  state: OfferDecisionState
}) => {
  const { lot, offering, saleMetrics } = listing
  const { values } = useFormikContext<MakeAnOfferFormValues>()
  const { renderOfferAmount } = useOfferAmount(listing)

  // All amounts in cents.
  const hasOffer = values.currentOfferAmount > 0
  const amount = hasOffer ? values.currentOfferAmount : values.minOfferAmount

  const msrp = lot.retailValue
  const viewCount = saleMetrics?.viewCount
  const isCanceled = state === 'canceled'
  const isEnded = state !== 'live' && state !== 'submitted'
  const endDate = parseOptionalISODate(
    isCanceled
      ? offering?.canceledAt || offering?.offerEndTime
      : offering?.offerEndTime
  )

  const percentOfMsrp = msrp ? amount / msrp : null

  const showMsrpRow = !!percentOfMsrp && getIsSpotPriceOffer(listing)

  return (
    <Header>
      <Column>
        {isEnded ? (
          <>
            <EndedValue>
              <FormattedMessage
                id={`${ns}.header.${isCanceled ? 'canceled' : 'ended'}`}
                values={{
                  date: endDate
                    ? formatInTimeZone(endDate, clientTimeZone, dateFormat)
                    : '—',
                }}
              />
            </EndedValue>
            {endDate && (
              <EndedValue>
                {formatInTimeZone(endDate, clientTimeZone, timeFormat)}
              </EndedValue>
            )}
          </>
        ) : (
          <>
            <Label>
              <FormattedMessage id={`${ns}.header.closesIn`} />
            </Label>
            <CountdownValue suppressHydrationWarning>
              {endDate ? <Duration format="short" target={endDate} /> : '—'}
            </CountdownValue>
          </>
        )}
        <SubRow>
          {typeof viewCount !== 'undefined' && (
            <StatItem>
              <FontAwesomeIcon icon={faEye} />
              <FormattedMessage
                id={`${ns}.header.views`}
                values={{ views: viewCount }}
              />
            </StatItem>
          )}
        </SubRow>
      </Column>

      <Column $alignRight>
        <Label>
          <FormattedMessage
            id={`${ns}.header.${hasOffer ? 'yourOffer' : 'listingPrice'}`}
          />
        </Label>
        <AmountValue>{renderOfferAmount(amount)}</AmountValue>
        {showMsrpRow && (
          <MsrpRow>
            <MsrpText>
              <FormattedMessage
                id={`${ns}.header.percentOfMsrp`}
                values={{
                  value: (
                    <FormattedNumber
                      value={percentOfMsrp}
                      style="percent"
                      minimumFractionDigits={1}
                      maximumFractionDigits={1}
                    />
                  ),
                }}
              />
            </MsrpText>
            <InfoIcon size="small" tooltipProps={{ placement: 'bottom' }}>
              <FormattedMessage id={`${ns}.header.percentOfMsrpTooltip`} />
            </InfoIcon>
          </MsrpRow>
        )}
      </Column>
    </Header>
  )
}
